import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { createContext } from "@compono/ui";
import {
  AuthProvider as OidcAuthProvider,
  hasAuthParams,
  useAuth,
} from "react-oidc-context";
import {
  SignoutResponse,
  User,
  UserManager,
  WebStorageStateStore,
} from "oidc-client-ts";
import { useVisibilityChange } from "@uidotdev/usehooks";
import { useSessionStorage } from "usehooks-ts";
import { useRouteOrgId } from "~/hooks/useRouteOrgId";
import { env } from "~/env";
import { extractOrgIdFromPath } from "../RouteOrgIdProvider";
import { useIframeDetection } from "../IframeDetection";

export type AuthenticationProviderProps = {
  children: React.ReactNode;
  userManager?: UserManager;
  onRedirectCallback?: (appState?: User | undefined) => void;
  autoSignin?: boolean;
  autoRenew?: boolean;
};

const DEFAULT_REDIRECT_URI = `${window.location.origin}/auth/callback`;
const DEFAULT_SIGNOUT_REDIRECT_URI = `${window.location.origin}/auth/signout-callback`;
const AUTH_CONTEXT_STORAGE_KEY = "_auth_context";
const AUTH_CONTEXT_STORAGE = window.sessionStorage;

type CachedAuthContext = {
  user?: User | null;
  orgId?: string | false;
};

/*
  AuthenticationProvider is a wrapper around AuthProvider
  We use it:
    - to configure Auth
    - as a facade to Auth to decouple our code from identity provider
    - customise the data format available via our useAuthentication hook
*/
export const AuthenticationProvider = (props: AuthenticationProviderProps) => {
  const {
    children,
    userManager,
    onRedirectCallback,
    autoSignin = true,
    autoRenew = true,
  } = props;

  const history = useHistory();
  /**
   * Take users back to the route they intended to access before authentication
   */
  const defaultOnRedirectCallback = (user: User | undefined) => {
    const returnTo =
      userReturnToDestination(user?.state) || window.location.pathname;
    console.log("Redirecting after signin to", returnTo);
    history.push(returnTo);
  };

  const defaultOnSignoutRedirectCallback = (
    response: SignoutResponse | undefined
  ) => {
    const { userState } = response || {};
    const returnTo = userReturnToDestination(userState) || "/";
    console.log("Redirecting after signout to", returnTo);
    history.push(returnTo);
  };

  const { isInIframe } = useIframeDetection();
  const enabledAutoSignin = !isInIframe && autoSignin;
  const enabledAutoRenew = autoRenew || false;

  return (
    <OidcAuthProvider
      {...(userManager
        ? { userManager }
        : {
            authority: env.AUTH_GATEWAY_OIDC_ISSUER,
            client_id: env.AUTH_CLIENT_ID,
            redirect_uri: DEFAULT_REDIRECT_URI,
            post_logout_redirect_uri: DEFAULT_SIGNOUT_REDIRECT_URI,
            scope: "openid email profile read:all",
            automaticSilentRenew: enabledAutoRenew,
            includeIdTokenInSilentRenew: true,
            userStore: new WebStorageStateStore({
              store: window.sessionStorage,
            }),
            stateStore: new WebStorageStateStore({
              store: window.localStorage,
            }),
            // resource="compono-api"
          })}
      onSignoutCallback={defaultOnSignoutRedirectCallback}
      matchSignoutCallback={(arg) => {
        return (
          arg.post_logout_redirect_uri ===
          `${window.location.origin}${window.location.pathname}`
        );
      }}
      onSigninCallback={onRedirectCallback || defaultOnRedirectCallback}
    >
      <InternalAuthContextProvider enabledAutoSignin={enabledAutoSignin}>
        <ExtractAuthContext />
        {enabledAutoSignin && <AutoSignin />}
        {enabledAutoRenew && <AutoRenew />}
        {!isInIframe && children}
      </InternalAuthContextProvider>
    </OidcAuthProvider>
  );
};

const userReturnToDestination = (state: unknown | undefined | null) => {
  if (
    !state ||
    !(typeof state === "object") ||
    !("returnTo" in state) ||
    !(typeof state.returnTo === "string")
  ) {
    return undefined;
  }

  return state.returnTo || undefined;
};

const ExtractAuthContext = () => {
  const auth = useAuth();
  const { isLoading, isAuthenticated, user } = auth;
  const {
    setCachedAuthContext,
    autoSigninState: { hasAutoSignedIn, shouldAutoSignin },
  } = useInternalAuthContext();
  const { isInIframe } = useIframeDetection();

  // user might be redirecting back after signin with returnTo state
  // we need to take that into account when determining the requested org id
  const returnTo = userReturnToDestination(user?.state);
  const { orgId: orgIdFromRoute } = useRouteOrgId();
  const requestedOrgId = returnTo
    ? extractOrgIdFromPath(returnTo)
    : orgIdFromRoute;

  // feels like this "url" should come from env/config
  const userAppData = useMemo(
    () => user?.profile["https://shortlyster.com/app_metadata"],
    [user]
  ) as UserAppData | undefined;

  // determine orgId
  // - undefined: we are waiting for auto signin to complete
  // - false: no valid orgId
  useEffect(() => {
    if (isInIframe) {
      // don't set orgId in iframe as it might be used for silent signin
      return;
    }

    // don't return orgId if we are about to auto signin, to wait for the updated userAppData
    // also not returning orgId unless user is authenticated
    if (isLoading || (shouldAutoSignin && !hasAutoSignedIn)) {
      // about to auto signin or renewing, wait for that to complete
      // set orgId to undefined if previously set to false, otherwise keep it as is

      setCachedAuthContext((prev) => ({
        ...prev,
        orgId: prev.orgId || undefined,
      }));
    } else {
      // determine orgId, set to false to indicate no valid orgId
      const orgId = userAppData
        ? getCurrentOrgId({ requestedOrgId, userAppData })
        : false;

      setCachedAuthContext((prev) => ({
        ...prev,
        orgId,
      }));
    }
  }, [
    requestedOrgId,
    userAppData,
    hasAutoSignedIn,
    isAuthenticated,
    isLoading,
    user,
    isInIframe,
  ]);

  // set user
  useEffect(() => {
    isAuthenticated &&
      user &&
      setCachedAuthContext((prev: CachedAuthContext) => ({
        ...prev,
        user,
      }));
  }, [user, isAuthenticated]);

  return null;
};

/**
 * This component automatically manages silent renew of tokens when the document is visible
 */
const AutoRenew = () => {
  const documentVisible = useVisibilityChange();
  const auth = useAuth();
  // disable auto renew when document is not visible
  useEffect(() => {
    if (!documentVisible || !auth.isAuthenticated) {
      auth.stopSilentRenew();
    } else {
      auth.startSilentRenew();
    }
  }, [documentVisible, auth.isAuthenticated]);

  return null;
};

/**
 * This component automatically tries to sign in the user when the app loads
 * to renew the tokens if the user is already signed in.
 * This is to ensure that latest permissions are loaded as it would be used to determine the orgId
 */
const AutoSignin = () => {
  const auth = useAuth();
  // detect if we are in an iframe
  // silent signin happens in an iframe so we don't want to do auto signin there
  const { isInIframe } = useIframeDetection();

  const {
    autoSigninState: { isAutoSigningIn, shouldAutoSignin },
    setAutoSigninState,
  } = useInternalAuthContext();

  // if we have auth params in the url (just signed in), we should not do silent signin
  useEffect(() => {
    if (hasAuthParams()) {
      setAutoSigninState("shouldAutoSignin", false);
    }
  }, [auth]);

  // automatically try to sign in when app loads to renew tokens
  useEffect(() => {
    if (
      !isInIframe && // don't need to do this in iframe (silent signin)
      !isAutoSigningIn && // don't need to do this if we are already auto signing in
      !auth.activeNavigator &&
      !auth.isLoading &&
      shouldAutoSignin
    ) {
      setAutoSigninState("isAutoSigningIn", true);
      auth
        .signinSilent()
        .then(() => setAutoSigninState("shouldAutoSignin", false))
        .finally(() => {
          setAutoSigninState("isAutoSigningIn", false);
          setAutoSigninState("hasAutoSignedIn", true);
        });
    }
  }, [auth, shouldAutoSignin, isInIframe]);

  return null;
};

type UserAppData = {
  id: string;
  accesses: {
    organisations: { organisationId: string; deactivated?: boolean }[];
  };
};

/*
  copied from services/shortlyster/service/src/utils/user.ts getActiveOrgs
  which is used by the `currentOrganisation` resolver in services/shortlyster/service/src/modules/organisations/resolvers.ts
*/
const getActiveOrgs = (user: UserAppData) =>
  user?.accesses?.organisations
    ?.filter((org) => org.deactivated !== true)
    ?.filter(Boolean) || [];

type GetCurrentOrgIdArg = {
  requestedOrgId?: string;
  userAppData: UserAppData;
};

/*
  getCurrentOrgId returns an organisation id to use in data requests.

  If the org id specified in the url is valid then we use that, otherwise we
  use the first org id available from userAppData.
*/
const getCurrentOrgId = (arg: GetCurrentOrgIdArg) => {
  const { requestedOrgId, userAppData } = arg;

  const orgIds = getActiveOrgs(userAppData).map(
    (org: { organisationId: string }) => org.organisationId
  );

  const defaultOrgId = orgIds?.[0] || false;
  const requestedOrgIdIsValid =
    requestedOrgId && orgIds.includes(requestedOrgId);

  return requestedOrgIdIsValid ? requestedOrgId : defaultOrgId;
};

type AutoSigninState = {
  isAutoSigningIn: boolean;
  hasAutoSignedIn: boolean;
  shouldAutoSignin: boolean;
};

type InternalAuthContext = {
  autoSigninState: AutoSigninState;
  setAutoSigninState: (key: keyof AutoSigninState, value: boolean) => void;

  cachedAuthContext: CachedAuthContext;
  setCachedAuthContext:
    | ((context: CachedAuthContext) => void)
    | ((fn: (prev: CachedAuthContext) => CachedAuthContext) => void);
};

const [useInternalAuthContext, InternalAuthContext] =
  createContext<InternalAuthContext>({
    name: "InternalAuthContext",
  });

const InternalAuthContextProvider = ({
  children,
  enabledAutoSignin = true,
}: PropsWithChildren<{ enabledAutoSignin?: boolean }>) => {
  const [cachedAuthContext, setCachedAuthContext] =
    useSessionStorage<CachedAuthContext>(AUTH_CONTEXT_STORAGE_KEY, {});

  const [autoSigninState, _setAutoSigninState] = useState<AutoSigninState>({
    isAutoSigningIn: false,
    hasAutoSignedIn: false,
    shouldAutoSignin: enabledAutoSignin,
  });

  const setAutoSigninState = (key: keyof AutoSigninState, value: boolean) => {
    _setAutoSigninState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <InternalAuthContext.Provider
      value={{
        autoSigninState,
        setAutoSigninState,
        cachedAuthContext,
        setCachedAuthContext,
      }}
    >
      {children}
    </InternalAuthContext.Provider>
  );
};

const readFromStorage = (key: string, storage: Storage) => {
  try {
    const item = storage.getItem(key);
    return item ? JSON.parse(item) : undefined;
  } catch (error) {
    return undefined;
  }
};

export const getCachedAuthContext = () => {
  const context = readFromStorage(
    AUTH_CONTEXT_STORAGE_KEY,
    AUTH_CONTEXT_STORAGE
  ) as CachedAuthContext | undefined;
  return context;
};

export const useAuthentication = () => {
  const auth = useAuth();
  const {
    isAuthenticated,
    signinRedirect,
    signoutRedirect,
    user,
    events,
    removeUser,
  } = auth;
  const { cachedAuthContext, setCachedAuthContext, autoSigninState } =
    useInternalAuthContext();
  const { isAutoSigningIn } = autoSigninState;
  const { orgId } = cachedAuthContext;

  const isLoading = isAutoSigningIn || auth.isLoading;
  const isLoadingOrNavigating = isLoading || auth.activeNavigator !== undefined;
  const isSessionExpired = user?.expired;

  const setOrgId = (newOrgId: string) => {
    setCachedAuthContext((prev) => ({
      ...prev,
      orgId: newOrgId,
    }));
  };

  return {
    logout: signoutRedirect,
    loginWithRedirect: signinRedirect,
    isAuthenticated,
    isLoading,
    isLoadingOrNavigating,
    isAutoSigningIn,
    isSessionExpired,
    orgId,
    setOrgId,
    user,
    events,
    removeUser,
  };
};
